/* eslint-disable @typescript-eslint/no-use-before-define */
import './comboform.scss';

import { Col } from 'react-bootstrap';
import { FaMinus, FaPlus } from 'react-icons/fa';

import Price from '@/resources/components/product/price/Price';
import { IFlatComboProduct } from '@/store/ducks/combos/types';
import { IFullProduct } from '@/store/ducks/products/types';

import { getDiscountPrice } from '../../utils/comboUtils';

const COLORS = {
  PRIMARY: '#007BFF',
  MUTED: '#8E8E8E',
  WHITE: 'white',
} as const;

export function ComboFormVariants({
  product,
  discount_percentage,
  maxQuantity = 10,
  min_quantity = 0,
  currentQuantity,
  onQuantityChange,
}: {
  product: IFlatComboProduct | IFullProduct;
  discount_percentage: number;
  maxQuantity?: number;
  min_quantity?: number;
  currentQuantity?: number;
  onQuantityChange: (quantity: number) => void;
}) {
  // Ensure currentQuantity defaults to min_quantity if not provided
  const effectiveQuantity =
    typeof currentQuantity === 'number' ? currentQuantity : min_quantity ?? 0;

  return (
    <>
      <Col xs={12} className="combo-product-card">
        <div>
          <ProductAddToCombo
            {...{
              product,
              discount_percentage,
              maxQuantity,
              currentQuantity: effectiveQuantity as number,
              onQuantityChange,
              min_quantity,
            }}
          />
        </div>
      </Col>
    </>
  );
}

function ProductAddToCombo({
  product,
  discount_percentage,
  maxQuantity,
  min_quantity,
  currentQuantity,
  onQuantityChange,
}: {
  product: IFlatComboProduct | IFullProduct;
  discount_percentage: number;
  maxQuantity: number;
  currentQuantity: number;
  min_quantity?: number;
  onQuantityChange: (quantity: number) => void;
}) {
  return (
    <Col xs={12} className="d-flex flex-row align-items-center w-100 pl-0 justify-content-between">
      <div className="d-flex flex-row align-items-start gap-1">
        <div className="image-wrapper position-relative align-items-start mx-0 pr-1 ">
          <img className="image" src={product.image ?? product?.images?.[0]} />
        </div>
        <div className="d-flex flex-column align-items-center w-75">
          <span className="text-break combo-title">{product.title}</span>
          <div className="align-self-start combo-product-price">
            <Price
              price={getDiscountPrice(product.price, discount_percentage)}
              defaultPrice={product.price}
            />
          </div>
        </div>
      </div>

      <QuantitySelector
        currentQuantity={currentQuantity}
        maxQuantity={maxQuantity}
        minQuantity={min_quantity}
        onQuantityChange={onQuantityChange}
      />
    </Col>
  );
}

export function QuantitySelector({
  currentQuantity,
  onQuantityChange,
  maxQuantity = 10,
  minQuantity = 0,
}: {
  currentQuantity: number;
  onQuantityChange?: (quantity: number) => void;
  maxQuantity: number;
  minQuantity?: number;
}) {
  const handleDecrease = () => {
    const newQuantity = Math.max(minQuantity, currentQuantity - 1);
    onQuantityChange?.(newQuantity);
  };

  const handleIncrease = () => {
    const newQuantity = Math.min(maxQuantity, currentQuantity + 1);
    onQuantityChange?.(newQuantity);
  };

  return (
    <div
      className={`d-flex align-items-end justify-content-end quantity-selector mt-4 ${
        currentQuantity === 0 ? 'collapsed' : ''
      }`}
      style={{
        transition: 'width 0.3s ease-in-out',
      }}
    >
      <button
        className={`btn btn-outline-secondary bg-light border-0 d-flex align-items-center justify-content-center quantity-button minus-button ml-1 ${
          currentQuantity === 0 ? 'hidden' : ''
        }`}
        onClick={handleDecrease}
        style={{
          transition: 'all 0.3s ease-in-out',
        }}
        disabled={currentQuantity <= minQuantity}
      >
        <FaMinus color={currentQuantity <= minQuantity ? COLORS.MUTED : COLORS.PRIMARY} />
      </button>

      <input
        type="number"
        value={currentQuantity}
        onChange={(e) => {
          const newQuantity = Math.max(0, Math.min(maxQuantity, parseInt(e.target.value) || 0));
          onQuantityChange?.(newQuantity);
        }}
        className={`text-center fw-bold quantity-text ${currentQuantity === 0 ? 'hidden' : ''}`}
        style={{
          minWidth: currentQuantity === 0 ? '0' : '24px',
          fontSize: '16px',
          transition: 'all 0.3s ease-in-out',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          padding: '0',
          margin: '0',
          MozAppearance: 'textfield',
          WebkitAppearance: 'none',
        }}
        min="0"
        max={maxQuantity}
      />

      <button
        className="btn btn-outline-secondary bg-light border-0 d-flex align-items-center justify-content-center quantity-button"
        onClick={handleIncrease}
        disabled={currentQuantity >= maxQuantity}
      >
        <FaPlus color={currentQuantity >= maxQuantity ? COLORS.MUTED : COLORS.PRIMARY} />
      </button>
    </div>
  );
}
